import React from 'react';
import PropTypes from 'prop-types';
import {
  Table, Card, Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { Field } from 'redux-form';
import classnames from 'classnames';
import Panel from '../../../../../shared/components/Panel';
import renderSelectField from '../../../../../shared/components/form/Select';
import {
  typeOptions,
  statusOptions,
  editRoleOptions,
  viewRoleOptions,
} from './utils';

export default function GroupSettingsPanel({
  initialLoad, getData, dynamicValueField, onReset, onDelete, onToggle, groups, activeTab, apiData, copyAccountSettings, changedFields, isUpdating, onBulkUpdate, onFieldChange,
}) {
  const changedCount = Object.keys(changedFields).length;

  return (
    <Panel lg={12} title="Settings By Group" getData={getData} parentRefresh={initialLoad}>
      <Card>
        <div className="profile__card tabs tabs--bordered-bottom">
          <div className="tabs__wrap">
            <Nav tabs>
              {groups && groups.map(group => (
                <NavItem key={group.value}>
                  <NavLink
                    className={classnames({ active: activeTab === group.label })}
                    onClick={() => {
                      onToggle(group.label);
                    }}
                  >
                    {group.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="Default" key="default">
                <p>Select tab above to start.</p>
              </TabPane>
              {groups && groups.map(group => (
                <TabPane tabId={group.label} key={group.value}>
                  <Table hover responsive className="table table--bordered">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Key</th>
                        <th>Value</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Edit Role</th>
                        <th>View Role</th>
                        <th>Group</th>
                        <th style={{ width: '100px' }}>Update</th>
                        <th style={{ width: '100px' }}>Create</th>
                        <th style={{ width: '160px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiData && apiData.filter(line => (((line.setting_group || '').toLowerCase() === group.value) || (group.value === ''))).map(line => (
                        <tr key={line.account_setting_id}>
                          <td>{line.account_setting_id}</td>
                          <td>
                            <span className="form">
                              <Field
                                name={`${line.account_setting_id}Key`}
                                component="input"
                                className="form"
                                onBlur={() => onFieldChange(line.account_setting_id, 'Key')}
                              />
                            </span>
                          </td>
                          <td>
                            <span className="form">
                              {dynamicValueField(`${line.account_setting_id}Value`, line.account_setting_id, () => onFieldChange(line.account_setting_id, 'Value'))}
                            </span>
                          </td>
                          <td>
                            <Field
                              name={`${line.account_setting_id}Type`}
                              component={renderSelectField}
                              type="text"
                              options={typeOptions}
                              onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Type', newValue)}
                            />
                          </td>
                          <td>
                            <Field
                              name={`${line.account_setting_id}Status`}
                              component={renderSelectField}
                              type="text"
                              options={statusOptions}
                              onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Status', newValue)}
                            />
                          </td>
                          <td>
                            <Field
                              name={`${line.account_setting_id}EditRole`}
                              component={renderSelectField}
                              type="text"
                              options={editRoleOptions}
                              onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'EditRole', newValue)}
                            />
                          </td>
                          <td>
                            <Field
                              name={`${line.account_setting_id}ViewRole`}
                              component={renderSelectField}
                              type="text"
                              options={viewRoleOptions}
                              onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'ViewRole', newValue)}
                            />
                          </td>
                          <td>
                            <Field
                              name={`${line.account_setting_id}Group`}
                              component={renderSelectField}
                              type="text"
                              options={groups}
                              onChange={(event, newValue) => onFieldChange(line.account_setting_id, 'Group', newValue)}
                            />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{line.last_updated_by} - {line.last_updated_ts}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{line.created_by} - {line.created_ts}</td>
                          <td>
                            <div style={{ display: 'grid', gridTemplateColumns: '70px 70px', gap: '12px' }}>
                              <div>
                                {changedFields[line.account_setting_id] && (
                                  <button type="button" className="badge badge-warning badge-btn" onClick={() => onReset(line.account_setting_id)}>Reset</button>
                                )}
                              </div>
                              <div>
                                <button type="button" className="badge badge-danger badge-btn" onClick={() => onDelete(line.account_setting_id, line.setting_key)}>Delete</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td><span>&nbsp;</span></td>
                      </tr>
                    </tbody>
                  </Table>
                </TabPane>
              ))}
            </TabContent>
            <p>Quick Actions:</p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onBulkUpdate()}
                disabled={isUpdating || changedCount === 0}
              >
                {isUpdating ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '5px' }} />
                    Updating...
                  </>
                ) : (
                  `Update All Changes ${changedCount > 0 ? `(${changedCount})` : ''}`
                )}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => copyAccountSettings()}>
                Copy Default System Account Settings
              </button>
            </div>
          </div>
        </div>
      </Card>
    </Panel>
  );
}

GroupSettingsPanel.propTypes = {
  initialLoad: PropTypes.bool.isRequired,
  getData: PropTypes.func.isRequired,
  dynamicValueField: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  groups: PropTypes.instanceOf(Array).isRequired,
  activeTab: PropTypes.string.isRequired,
  apiData: PropTypes.instanceOf(Array).isRequired,
  copyAccountSettings: PropTypes.func.isRequired,
  changedFields: PropTypes.instanceOf(Object).isRequired,
  isUpdating: PropTypes.bool.isRequired,
  onBulkUpdate: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};
